import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Clock,
  Plus,
  Loader2,
  StickyNote,
} from "lucide-react";

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const { api, business } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    if (business?.id && customerId) {
      fetchCustomerDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, customerId]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await api.get(
        `/businesses/${business.id}/customers/${customerId}`,
      );
      setCustomer(response.data.customer);
      setAppointments(response.data.appointments);
      setNotes(response.data.notes);
    } catch (error) {
      console.error("Failed to fetch customer details:", error);
      toast.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setAddingNote(true);
    try {
      await api.post(
        `/businesses/${business.id}/customers/${customerId}/notes`,
        {
          content: newNote,
        },
      );
      toast.success("Note added");
      setNewNote("");
      fetchCustomerDetails();
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      booked: "badge-confirmed",
      pending: "badge-pending",
      cancelled: "badge-cancelled",
      completed: "badge-completed",
      no_show: "badge-cancelled",
    };
    return badges[status] || "badge-pending";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">
            Loading customer details...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="font-heading text-2xl font-bold mb-2">
            Customer not found
          </h2>
          <Link to="/dashboard/customers">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Customers
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="customer-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/dashboard/customers">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Customer Info */}
          <div className="lg:col-span-1">
            <div className="card-base">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl">
                    {(customer.name?.[0] || customer.email[0]).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="font-heading text-xl font-bold">
                    {customer.name || "Unnamed Customer"}
                  </h1>
                  <span className="text-sm text-muted-foreground">
                    Customer
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{customer.email}</span>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{customer.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">
                    {appointments.length} total appointments
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card-base mt-6">
              <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <StickyNote className="w-5 h-5" />
                Internal Notes
              </h2>

              <div className="space-y-3 mb-4">
                <Textarea
                  placeholder="Add a private note about this customer..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  data-testid="add-note-input"
                />
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  data-testid="add-note-btn"
                >
                  {addingNote ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Note
                </Button>
              </div>

              {notes.length > 0 && (
                <div className="space-y-3 border-t border-border pt-4">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 rounded-lg bg-secondary/50"
                    >
                      <p className="text-sm">{note.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(note.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Appointment History */}
          <div className="lg:col-span-2">
            <div className="card-base">
              <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Appointment History
              </h2>

              {appointments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No appointments yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="flex items-start gap-4 p-4 rounded-lg border border-border"
                    >
                      <div className="text-center min-w-[70px]">
                        <div className="text-sm font-medium">
                          {new Date(appt.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(appt.date).getFullYear()}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-medium">{appt.service_name}</h3>
                          <span className={getStatusBadge(appt.status)}>
                            {appt.status}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {appt.time} - {appt.end_time} ({appt.duration} min)
                        </div>
                        <div className="text-sm font-accent font-semibold mt-1">
                          ${appt.service_price.toFixed(2)}
                        </div>
                        {appt.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            "{appt.notes}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerDetailPage;
